import { X, Download, FileText, Image, ZoomIn, ZoomOut, RotateCw } from 'lucide-react'
import { useState, useEffect } from 'react'

interface FileViewerModalProps {
  isOpen: boolean
  file: {
    id: number
    filename: string
    content_type: string
    byte_size: number
    url: string
  } | null
  onClose: () => void
  onDownload: (url: string, filename: string) => void
}

export default function FileViewerModal({ isOpen, file, onClose, onDownload }: FileViewerModalProps) {
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)

  const isImage = file?.content_type?.startsWith('image/')
  const isPdf = file?.content_type === 'application/pdf'

  useEffect(() => {
    if (isOpen && file?.url) {
      setLoading(true)
      setError(null)
      setZoom(1)
      setRotation(0)

      // Build the full URL with auth headers
      const token = localStorage.getItem('authToken')
      const companyId = localStorage.getItem('companyId')
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
      const fullUrl = file.url.startsWith('http') ? file.url : `${baseUrl.replace('/api/v1', '')}${file.url}`

      // Fetch the file as a blob
      fetch(fullUrl, {
        method: 'GET',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(companyId && { 'X-Company-ID': companyId }),
        },
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to load file: ${response.statusText}`)
          }
          return response.blob()
        })
        .then(blob => {
          const blobUrl = URL.createObjectURL(blob)
          setFileUrl(blobUrl)
          setLoading(false)
        })
        .catch(err => {
          setError(err.message)
          setLoading(false)
        })
    }

    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl)
      }
    }
  }, [isOpen, file?.url])

  // Cleanup on close
  useEffect(() => {
    if (!isOpen && fileUrl) {
      URL.revokeObjectURL(fileUrl)
      setFileUrl(null)
    }
  }, [isOpen])

  if (!isOpen || !file) return null

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3))
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.25))
  const handleRotate = () => setRotation(prev => (prev + 90) % 360)

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
              {isImage ? (
                <Image className="w-5 h-5 text-cyan-400" />
              ) : isPdf ? (
                <FileText className="w-5 h-5 text-cyan-400" />
              ) : (
                <FileText className="w-5 h-5 text-cyan-400" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-white truncate">
                {file.filename}
              </h3>
              <p className="text-xs text-slate-500">
                {(file.byte_size / 1024).toFixed(1)} KB • {file.content_type}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Image controls */}
            {isImage && fileUrl && !loading && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  title="Zoom out"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-xs text-slate-500 min-w-[3rem] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  title="Zoom in"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  title="Rotate"
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                <div className="w-px h-6 bg-slate-700 mx-1" />
              </>
            )}

            {/* Download button */}
            <button
              onClick={() => onDownload(file.url, file.filename)}
              className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Download</span>
            </button>

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-slate-950/50">
          {loading ? (
            <div className="flex flex-col items-center gap-3 text-slate-400">
              <div className="w-8 h-8 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
              <p className="text-sm">Loading file...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 text-red-400">
              <FileText className="w-12 h-12" />
              <p className="text-sm">{error}</p>
              <button
                onClick={() => onDownload(file.url, file.filename)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Download instead
              </button>
            </div>
          ) : fileUrl ? (
            isImage ? (
              <div className="overflow-auto max-w-full max-h-full flex items-center justify-center">
                <img
                  src={fileUrl}
                  alt={file.filename}
                  className="max-w-none transition-transform duration-200"
                  style={{
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                  }}
                />
              </div>
            ) : isPdf ? (
              <iframe
                src={fileUrl}
                className="w-full h-full min-h-[70vh] rounded-lg border border-slate-700"
                title={file.filename}
              />
            ) : (
              <div className="flex flex-col items-center gap-3 text-slate-400">
                <FileText className="w-16 h-16" />
                <p className="text-sm">Preview not available for this file type</p>
                <button
                  onClick={() => onDownload(file.url, file.filename)}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download file
                </button>
              </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  )
}
